import { useLocation, useNavigate } from "react-router-dom";
import { OrderStatusFilters } from "@jasper/shared";
import { OrderStatus } from "../../../generated/graphql";
import { INBOX_STATUSES, TO_SHIP_STATUSES } from "../../../constants/orders";
import { useOrderListFilter } from "../../../pages/home/contexts/OrderListFilterContext";
import { getTodayTimestamp } from "../../../utils/date.utils";

export const useStatusFilters = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    setFilters,
    clearFilters,
    statuses: filteredStatuses,
    isArchived,
    lastShippingDate,
  } = useOrderListFilter();

  const onOrdersList = pathname === "/home";
  const hasNoDateFilter = !lastShippingDate?.start && !lastShippingDate?.end;
  const hasNoExtraFilters = !isArchived && hasNoDateFilter;

  const isSingleStatus = (status: OrderStatus) =>
    onOrdersList &&
    hasNoExtraFilters &&
    filteredStatuses?.length === 1 &&
    filteredStatuses?.[0] === status;

  const isAllActive =
    onOrdersList && !filteredStatuses?.length && hasNoExtraFilters;

  const isInboxActive =
    onOrdersList &&
    hasNoExtraFilters &&
    filteredStatuses?.every(s => INBOX_STATUSES.includes(s)) &&
    INBOX_STATUSES.every(s => filteredStatuses?.includes(s));

  const endsToday =
    !!lastShippingDate?.end &&
    new Date(lastShippingDate.end).toDateString() === new Date().toDateString();

  const isToShipActive =
    onOrdersList &&
    filteredStatuses?.every(s => TO_SHIP_STATUSES.includes(s)) &&
    TO_SHIP_STATUSES.every(s => filteredStatuses?.includes(s)) &&
    isArchived === false &&
    endsToday &&
    !lastShippingDate?.start;

  const handleStatusFilterChange = (
    newStatus: OrderStatus | OrderStatusFilters
  ) => {
    navigate("/home");

    clearFilters();

    switch (newStatus) {
      case OrderStatusFilters.INBOX:
        setFilters({ statuses: INBOX_STATUSES, isArchived: false });
        break;
      case OrderStatusFilters.TO_SHIP:
        setFilters({
          statuses: TO_SHIP_STATUSES,
          lastShippingDate: { end: new Date(getTodayTimestamp()) },
          isArchived: false,
        });
        break;
      case OrderStatusFilters.ARCHIVED:
        setFilters({ isArchived: true });
        break;
      case OrderStatusFilters.ALL:
        clearFilters();
        break;
      default:
        setFilters({ statuses: [newStatus] });
    }
  };

  return {
    isSingleStatus,
    isAllActive,
    isInboxActive,
    isToShipActive,
    handleStatusFilterChange,
  };
};
